"use client";

import dynamic from "next/dynamic";
import { useEffect, useState, type ReactNode } from "react";

/**
 * Gate for the WebGL hero. Server HTML (and therefore no-JS visitors, bots,
 * and the pre-hydration paint) always contains the static fallback passed in
 * from the server component tree. After mount we upgrade to the WebGL scene
 * only when the visitor has WebGL and hasn't asked for reduced motion.
 */

const HeroSceneCanvas = dynamic(() => import("./scene"), {
  ssr: false,
  // while the 3D chunk streams in, the page keeps showing the gate's
  // fallback (we render it underneath until the canvas mounts)
  loading: () => null,
});

function supportsHardwareWebGL(): boolean {
  try {
    const canvas = document.createElement("canvas");
    const gl = (canvas.getContext("webgl2") || canvas.getContext("webgl")) as WebGLRenderingContext | null;
    if (!gl) return false;
    // Software rasterizers (SwiftShader / llvmpipe — blocklisted GPUs,
    // headless environments, some VMs) report WebGL support but render
    // garbage or burn CPU. Those visitors get the static hero instead.
    const dbg = gl.getExtension("WEBGL_debug_renderer_info");
    const renderer = dbg
      ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL))
      : String(gl.getParameter(gl.RENDERER));
    return !/swiftshader|llvmpipe|software|basic render/i.test(renderer);
  } catch {
    return false;
  }
}

/** One optimized variant, shared by every print in the scene. */
export const SCENE_TEXTURE_URL = `/_next/image?url=${encodeURIComponent("/brand/hero.jpg")}&w=1080&q=75`;

export function Hero3D({ fallback }: { fallback: ReactNode }) {
  const [mode, setMode] = useState<"fallback" | "webgl">("fallback");
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [sceneReady, setSceneReady] = useState(false);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (!supportsHardwareWebGL()) return;
    // Deterministic upgrade: only mount the WebGL scene once the shared
    // texture image is actually decoded — the canvas never shows empty.
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      setImage(img);
      setMode("webgl");
    };
    img.src = SCENE_TEXTURE_URL;
    return () => {
      cancelled = true;
    };
  }, []);

  if (mode === "fallback" || !image) return <>{fallback}</>;
  return (
    <div className="relative h-full w-full">
      {/* the static composition stays behind the canvas until real frames
          flow (never an empty hero), then fades so it can't ghost through
          transparent regions while the scene moves */}
      <div
        className={`absolute inset-0 transition-opacity duration-700 ${sceneReady ? "opacity-0" : "opacity-100"}`}
      >
        {fallback}
      </div>
      <div className="absolute inset-0">
        <HeroSceneCanvas
          image={image}
          onReady={() => setSceneReady(true)}
          onContextLost={() => {
            setSceneReady(false);
            setMode("fallback");
          }}
        />
      </div>
    </div>
  );
}
